import { saveAs } from 'file-saver'
import toast, { Toast, ToastPosition } from 'react-hot-toast'
import sanitize from 'sanitize-filename'
import { supabase } from 'src/clientLib/supabase'
import { Workflow } from 'src/redux/consts'
import { RootState } from 'src/redux/root'
import { ubemIoApi } from 'src/redux/services/ubem'
import { setError } from 'src/redux/slices/error/reducers'
import { Project, Scenario, TemplateLibrary } from 'src/types/database-model'

import { setTarget } from '../analysis/reducers'
import {
  CreateScenarioArg,
  DeleteProjectArg,
  DeleteScenarioArg,
  DeleteTemplateLibraryArg,
  DeleteTemplateLibraryRes,
  DownloadScenarioModelArgs,
  SurveyCityTargetFields,
  UpdateProjectArg,
  UpdateScenarioArg,
  UploadGISFileArg,
  UploadTemplateLibraryArg,
  UploadTemplateLibraryRes,
  UploadUmiResultsFileArg,
} from './types'
import { dbTargetToReduxTargets } from './utils'

const toastOptions = (
  endpoint: keyof typeof projectApi.endpoints,
  additional?: Partial<Pick<Toast, 'id' | 'style' | 'className'>>,
) => ({
  id: endpoint,
  position: 'bottom-center' as ToastPosition,
  ...additional,
  style: { minWidth: '30%' },
})

export const mutationFixedCacheKeys = {}

const workflow = Workflow.project

export const projectApi = ubemIoApi.injectEndpoints({
  endpoints: (builder) => ({
    testToken: builder.query({
      query: () => ({
        url: '/api/test_with_token',
      }),
      onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
        try {
          const res = await queryFulfilled
        } catch ({ error }) {
          dispatch(
            setError({
              error,
              workflow: Workflow.project,
              key: 'test token and download',
            }),
          )
        }
      },
    }),
    getProject: builder.query<Project | null, ''>({
      queryFn: async (_, { getState }) => {
        const state = getState() as RootState
        const { id } = state.project
        if (!id) return { data: null }
        const { data, error } = await supabase.from('projects').select('*').eq('id', id)
        if (error) throw error
        return { data: data?.[0] ?? null }
      },
      onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
        try {
          const res = await queryFulfilled
        } catch ({ error }) {
          dispatch(
            setError({
              error,
              workflow: Workflow.project,
              key: 'getProject',
            }),
          )
        }
      },
      providesTags: ['Active Project'],
    }),
    updateProject: builder.mutation<Project, UpdateProjectArg>({
      queryFn: async (arg, { getState }) => {
        const state = getState() as RootState
        const id = state.project.id
        if (!id) throw { message: "can't update a project when no project is active." }
        const { data: projects, error } = await supabase
          .from('projects')
          .update(arg)
          .eq('id', id)
          .select()
        if (error) throw error
        if (!projects || projects?.length === 0) throw { message: 'No project was updated.' }
        const data = projects[0]
        return { data }
      },
      onQueryStarted: async (arg, { getState, dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled
          const keys = Object.keys(arg) as Array<keyof typeof arg>
          const state = getState() as RootState
          const analysisIsFromCloud = state.analysis.fromCloud
          const cityTargetKeys = [
            'city_near_year',
            'city_far_year',
            'city_near_percent',
            'city_far_percent',
          ]
          keys.forEach((key) => {
            if (analysisIsFromCloud && cityTargetKeys.includes(key)) {
              const { parameter, target } = dbTargetToReduxTargets(key as SurveyCityTargetFields)
              dispatch(
                setTarget({
                  target,
                  parameter,
                  value: arg[key] as number,
                }),
              )
            }
          })
        } catch ({ error }) {
          dispatch(
            setError({
              error,
              workflow: Workflow.project,
              key: 'updateProject',
            }),
          )
        }
      },
      invalidatesTags: ['Active Project', 'Scenarios'],
    }),
    downloadScenarioModel: builder.mutation<string, DownloadScenarioModelArgs>({
      queryFn: async ({ title, source_umi_file_version, source_umi_file_location }) => {
        if (!source_umi_file_location) throw { message: 'No download location provided.' }
        const { data, error } = await supabase.storage
          .from('umi-files')
          .download(source_umi_file_location)
        if (error) throw error
        if (!data) throw { message: 'Could not find file in cloud storage.' }
        saveAs(data, `${title}_v${String(source_umi_file_version).padStart(2, '0')}.umi`)
        return { data: 'successfully downloaded file' }
      },
      onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
        try {
          toast.loading('Downloading model file...', toastOptions('downloadScenarioModel'))
          const res = await queryFulfilled
          toast.dismiss('downloadScenarioModel')
        } catch ({ error }) {
          toast.dismiss('downloadScenarioModel')
          dispatch(
            setError({
              error,
              workflow: Workflow.project,
              key: 'downloadScenarioModel',
            }),
          )
        }
      },
    }),
    uploadUmiResultsFile: builder.mutation<Scenario[] | null, UploadUmiResultsFileArg>({
      queryFn: async (arg, { getState }) => {
        const { id, project_id, results_umi_file_version, file } = arg
        const filename = sanitize(file.name, { replacement: '_' })
        const { data: fileUploadData, error: fileUploadError } = await supabase.storage
          .from('umi-files')
          .upload(
            `${project_id}/results/scenarios/${String(id).padStart(2, '0')}/v${String(
              results_umi_file_version + 1,
            ).padStart(2, '0')}/${filename}`,
            file,
            {
              upsert: false,
            },
          )
        if (fileUploadError) throw fileUploadError
        if (!fileUploadData) throw { message: 'No file upload data returned.' }
        const { data, error } = await supabase
          .from('scenarios')
          .update({
            results_umi_file_location: fileUploadData.path,
            results_umi_file_version: results_umi_file_version + 1,
            aggregated_energy_use_data: null,
          })
          .eq('id', id)
          .select()
        if (error) throw error
        return { data }
      },
      onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
        try {
          const res = await queryFulfilled
        } catch ({ error }) {
          dispatch(
            setError({
              error,
              workflow: Workflow.project,
              key: 'uploadUmiResultsFile',
            }),
          )
        }
      },
      invalidatesTags: ['Scenarios', 'Active Project'],
    }),
    uploadGISFile: builder.mutation<Project[] | null, UploadGISFileArg>({
      queryFn: async ({ id, organization_id, gis_file_version, file }, { getState }) => {
        const filename = sanitize(file.name, { replacement: '_' })
        const { data: fileUploadData, error: fileUploadError } = await supabase.storage
          .from('gis-files')
          .upload(
            `${organization_id}/${id}/original/v${String(gis_file_version + 1).padStart(
              2,
              '0',
            )}/${filename}`,
            file,
            {
              upsert: true,
            },
          )
        if (fileUploadError) throw fileUploadError
        if (!fileUploadData) throw { message: 'No file upload data returned.' }
        const { data, error } = await supabase
          .from('projects')
          .update({
            gis_file_location: fileUploadData.path,
            gis_file_version: gis_file_version + 1,
          })
          .eq('id', id)
          .select()
        if (error) throw error
        return { data }
      },
      onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
        try {
          const res = await queryFulfilled
        } catch ({ error }) {
          dispatch(
            setError({
              error,
              workflow: Workflow.project,
              key: 'uploadGISfile',
            }),
          )
        }
      },
      invalidatesTags: ['Active Project', 'Scenarios'],
    }),
    downloadOrgTemplateLibrary: builder.query<string, string>({
      queryFn: async (file_location) => {
        const { data, error } = await supabase.storage
          .from('template-libraries')
          .download(file_location)
        if (error) throw error
        const tokens = file_location.split('/')
        const filename = sanitize(tokens[tokens.length - 1], { replacement: '_' })
        if (!data) throw { message: 'Could not find a file with the specified name.' }
        saveAs(data, filename)
        return { data: 'successfully downloaded file' }
      },
      onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
        try {
          toast.loading(
            'Downloading template library file...',
            toastOptions('downloadOrgTemplateLibrary'),
          )
          const res = await queryFulfilled
          toast.dismiss('downloadOrgTemplateLibrary')
        } catch ({ error }) {
          toast.dismiss('downloadOrgTemplateLibrary')
          dispatch(
            setError({
              error,
              workflow: Workflow.project,
              key: 'downloadOrgTemplateLibrary',
            }),
          )
        }
      },
      providesTags: ['Org Template Libraries'],
    }),
    getOrgTemplateLibraries: builder.query<TemplateLibrary[], ''>({
      queryFn: async (_, { getState }) => {
        const state = getState() as RootState
        const organization = state.user.activeOrganization
        if (!organization) return { data: [] }
        const { data, error } = await supabase
          .from('template_libraries')
          .select('*')
          .eq('organization', organization)
        if (error) throw error
        if (!data) return { data: [] }
        return { data }
      },
      onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled
        } catch ({ error }) {
          dispatch(
            setError({
              error,
              workflow: Workflow.project,
              key: 'getOrgTemplateLibraries',
            }),
          )
        }
      },
      providesTags: ['Org Template Libraries'],
    }),
    deleteOrgTemplateLibrary: builder.mutation<DeleteTemplateLibraryRes, DeleteTemplateLibraryArg>({
      queryFn: async ({ id, file_location }, { getState }) => {
        const { data: projectsData, error: projectsError } = await supabase
          .from('projects')
          .select('id,title')
          .eq('template_lib_id', id)
        if (projectsError) throw projectsError
        if (projectsData && projectsData.length > 0)
          throw {
            message: `Cannot delete this template library because it is used by the following projects: ${projectsData
              .map(({ title }) => title)
              .join(', ')}`,
          }
        const { data: deleteData, error: deleteError } = await supabase.storage
          .from('template-libraries')
          .remove([file_location])
        if (deleteError) throw deleteError
        const { data, error } = await supabase.from('template_libraries').delete().eq('id', id)
        if (error) throw error
        return { data: true }
      },
      onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled
        } catch ({ error }) {
          dispatch(
            setError({
              error,
              workflow: Workflow.project,
              key: 'deleteOrgTemplateLibrary',
            }),
          )
        }
      },
      invalidatesTags: ['Org Template Libraries'],
    }),
    uploadOrgTemplateLibrary: builder.mutation<UploadTemplateLibraryRes, UploadTemplateLibraryArg>({
      queryFn: async (arg, { getState }) => {
        const { title, file, public: isPublic } = arg
        const state = getState() as RootState
        const organization_id = state.user.activeOrganization
        if (!organization_id) throw { message: 'No organization selected!' }
        const filename = sanitize(file.name, { replacement: '_' })
        const cloud_destination = `${organization_id}/${filename}`
        const { data: uploadResult, error: uploadError } = await supabase.storage
          .from('template-libraries')
          .upload(cloud_destination, file, { upsert: false })
        // TODO: catch upsert error more cleverly
        if (uploadError) throw uploadError
        const { data, error } = await supabase
          .from('template_libraries')
          .insert({
            file_location: cloud_destination,
            organization: organization_id,
            title,
            public: isPublic,
          })
          .select()
        if (error) throw error
        return { data }
      },
      onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled
        } catch ({ error }) {
          dispatch(
            setError({
              error,
              workflow: Workflow.project,
              key: 'uploadOrgTemplateLibrary',
            }),
          )
        }
      },
      invalidatesTags: ['Org Template Libraries'],
    }),
    getScenarios: builder.query<Scenario[] | null, ''>({
      queryFn: async (_, { getState }) => {
        const state = getState() as RootState
        const project_id = state.project.id
        if (project_id === null || project_id === undefined) return { data: null }
        const { data, error } = await supabase
          .from('scenarios')
          .select('*')
          .eq('project_id', project_id)
          .order('sort_order', { ascending: true })
        if (error) throw error
        return {
          data,
        }
      },
      onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
        try {
          const res = await queryFulfilled
        } catch ({ error }) {
          dispatch(
            setError({
              error,
              workflow: Workflow.project,
              key: 'getScenarios',
            }),
          )
        }
      },
      providesTags: ['Scenarios', 'Active Project'],
    }),
    createScenario: builder.mutation<Scenario[] | null, CreateScenarioArg>({
      queryFn: async (scenario: CreateScenarioArg, { getState }) => {
        const state = getState() as RootState
        const project_id = state.project.id
        if (!project_id) throw { message: 'No scenario selected' }
        const { data, error } = await supabase
          .from('scenarios')
          .insert({ ...scenario, project_id })
          .select()
        if (error) throw error
        return { data }
      },
      onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
        try {
          const res = await queryFulfilled
        } catch ({ error }) {
          dispatch(
            setError({
              error,
              workflow: Workflow.project,
              key: 'createScenario',
            }),
          )
        }
      },
      invalidatesTags: ['Active Project', 'Scenarios', 'Org Projects'],
    }),
    updateScenario: builder.mutation<Scenario[] | null, UpdateScenarioArg>({
      queryFn: async (arg, { getState }) => {
        const { id, title } = arg
        const { update } = arg
        if (id) {
          const { data, error } = await supabase
            .from('scenarios')
            .update(update)
            .eq('id', id)
            .select()
          if (error) throw error
          return { data }
        }
        if (title) {
          const state = getState() as RootState
          const project_id = state.project.id
          const { data, error } = await supabase
            .from('scenarios')
            .update(update)
            .eq('project_id', project_id)
            .eq('title', title)
            .select()
          if (error) throw error
          return { data }
        }
        throw Error("Must supply either 'id' or 'title' when updating a scenario.")
      },
      onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled
        } catch ({ error }) {
          dispatch(
            setError({
              error,
              workflow: Workflow.project,
              key: 'updateScenario',
            }),
          )
        }
      },
      invalidatesTags: ['Active Project', 'Scenarios', 'Org Projects'],
    }),
    // TODO: add file deletion
    deleteScenario: builder.mutation<Scenario[], DeleteScenarioArg>({
      queryFn: async (id) => {
        const { data, error } = await supabase.from('scenarios').delete().eq('id', id).select()
        if (error) throw error
        if (!data) return { data: [] }
        return { data }
      },
      onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
        try {
          const res = await queryFulfilled
        } catch ({ error }) {
          dispatch(
            setError({
              error,
              workflow: Workflow.project,
              key: 'deleteScenario',
            }),
          )
        }
      },
      invalidatesTags: ['Active Project', 'Scenarios'],
    }),
    // TODO: add file deletion
    deleteProject: builder.mutation<Project[] | null, DeleteProjectArg>({
      queryFn: async ({ id, includeFiles }) => {
        const { data: scenarioData, error: scenarioError } = await supabase
          .from('scenarios')
          .delete()
          .eq('project_id', id)
          .select()
        if (scenarioError) throw scenarioError
        const { data, error } = await supabase.from('projects').delete().eq('id', id).select()
        if (error) throw error
        return { data }
      },
      onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
        try {
          await queryFulfilled
        } catch ({ error }) {
          dispatch(
            setError({
              error,
              workflow: Workflow.project,
              key: 'deleteProject',
            }),
          )
        }
      },
      invalidatesTags: ['Active Project', 'Scenarios', 'Org Projects'],
    }),
  }),
})

export const {
  testToken,
  getProject,
  updateProject,
  deleteProject,
  uploadGISFile,
  getScenarios,
  createScenario,
  updateScenario,
  deleteScenario,
  uploadUmiResultsFile,
  downloadScenarioModel,
  getOrgTemplateLibraries,
  uploadOrgTemplateLibrary,
  deleteOrgTemplateLibrary,
  downloadOrgTemplateLibrary,
} = projectApi.endpoints
